import { JsonPipe, NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input, signal } from "@angular/core";
import { SchoolHealthScore } from "src/app/core/services/partner-success.service";
import { SortColumn, sortUpdate } from "src/app/core/utils/table";
import { ColorCategoryComponent } from "src/app/shared/components/color-category/color-category.component";

@Component({
  selector: 'wf-partner-success-health',
  imports: [
    NgTemplateOutlet,
    ColorCategoryComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <table class="report-table report-table-white">
      <colgroup>
        <col style="width: 15%;"/>
        <col style="width: 15%;"/>
        <col/>
        <col/>
        <col/>
        <col/>
        <col/>
        <col/>
      </colgroup>
      <thead>
        <ng-template #sortableTableHeader let-column="column">
          <th class="sortable-report-table-header">
            <button
              (click)="updateSort(column)"
              [attr.aria-sort]="sortColumn().column === column ? (sortColumn().reverse ? 'desending' : 'ascending') : undefined"
            >
              {{ column }}
              <div
                [class]=" { 'arrow-up' : sortColumn().reverse, 'arrow-down': !sortColumn().reverse }"
                [style.visibility]="(sortColumn().column !== column) ? 'hidden' : 'visible'"
                class="print-hide"
              ></div>
            </button>
          </th>
        </ng-template>
        <tr>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.DISTRICT }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.SCHOOL }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.OVERALL_HEALTH }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.SCHOOL_LOGIN }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.TEACHER_LOGIN }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.STUDENT_USAGE }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.TEACHERS }"/>
          <ng-container [ngTemplateOutlet]="sortableTableHeader" [ngTemplateOutletContext]="{ column: COLUMNS.STUDENTS }"/>
        </tr>
      </thead>
      <tbody>
        @for (score of scoresSorted(); track score.school.schoolID) {
          <tr>
            <td>{{ score.district.name }}</td>
            <th scope="row">{{ score.school.name }}</th>
            <td>
              @let level = score.overallHealth >= 80 ? 'green' : (score.overallHealth < 60 ? 'red' : 'yellow');
              <wf-color-category [level]="level">
                {{ score.overallHealth }}
              </wf-color-category>
            </td>
            <td> {{ score.resourceHealth }} </td>
            <td> {{ score.loginHealth }} </td>
            <td> {{ score.studentUsageHealth }} </td>
            <td> {{ score.teacherCount }} </td>
            <td> {{ score.studentCount }} </td>
          </tr>
        }
      </tbody>
    </table>
  `,
})
export class PartnerSuccessHealthComponent {
  readonly scores = input.required<SchoolHealthScore[]>();

  readonly COLUMNS = {
    DISTRICT: 'District',
    SCHOOL: 'School',
    OVERALL_HEALTH: 'Overall Health',
    SCHOOL_LOGIN: 'School User Login (20)',
    TEACHER_LOGIN: 'Teacher Logins (30)',
    STUDENT_USAGE: 'Student Usage (50)',
    TEACHERS: '# Teachers',
    STUDENTS: '# Students',
  } as const;

  readonly sortColumn = signal<SortColumn>({
    column: this.COLUMNS.DISTRICT,
    reverse: false,
  });

  readonly scoresSorted = computed(() => {
    const { column, reverse } = this.sortColumn();

    let cmp = (s1: SchoolHealthScore, s2: SchoolHealthScore) => {
      const districtThenSchool = s1.district.name.localeCompare(s2.district.name) || s2.school.name.localeCompare(s2.school.name);

      let comp = districtThenSchool;
      if (column === this.COLUMNS.SCHOOL) {
        comp = s1.school.name.localeCompare(s2.school.name);
      } else if (column === this.COLUMNS.OVERALL_HEALTH) {
        comp = s1.overallHealth - s2.overallHealth;
      } else if (column === this.COLUMNS.SCHOOL_LOGIN) {
        comp = s1.resourceHealth - s2.resourceHealth;
      } else if (column === this.COLUMNS.TEACHER_LOGIN) {
        comp = s1.loginHealth - s2.loginHealth;
      } else if (column === this.COLUMNS.STUDENT_USAGE) {
        comp = s1.studentUsageHealth - s2.studentUsageHealth;
      } else if (column === this.COLUMNS.TEACHERS) {
        comp = s1.teacherCount - s2.teacherCount;
      } else if (column === this.COLUMNS.STUDENTS) {
        comp = s1.studentCount - s2.studentCount;
      }

      if (comp === 0 && column !== this.COLUMNS.DISTRICT) {
        // On tie of non-district columns, fall back to ascending name
        // This ignores reverse. Reverse should only affect the top level sort, not the tiebreaker.
        return districtThenSchool
      }

      return reverse ? (comp * -1) : comp
    }

    return [...this.scores()].sort(cmp);
  });

  updateSort(clickedColumn: string) {
    this.sortColumn.update(sortUpdate(clickedColumn));
  }
}
